<template>
    <div class="text-center">
        <v-dialog
            v-model="isDialogOpen"
            width="550"
        >
            <v-card v-if="product">
                <v-toolbar
                    flat
                >
                    <v-col class="d-flex justify-space-around">
                        <v-toolbar-title class="pl-6">
                            <span class="font-weight-bold">{{ $t('selectProductQuantity') }}</span>
                        </v-toolbar-title>
                    </v-col>
                    <v-toolbar-items class="pr-2 ml-auto">
                        <v-btn
                            icon
                            @click.native="$emit('close')"
                        >
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-container>
                    <v-row class="px-3">
                        <v-col cols="4">
                            <img
                                :src="getImageThumbSrc(product.images)"
                                :alt="`image-${product.id}`"
                                class="product-img"
                            />
                        </v-col>
                        <v-col cols="8">
                            <div class="text-h5 font-weight-bold">{{product.productName}}</div>
                            <router-link
                                class="text-underline"
                                v-if="product.seller.companyID"
                                :to="`/companies/${product.seller.id}`"
                            >
                                {{ sellerTitle }}
                            </router-link>
                            <div
                                v-else
                                class="grey--text text--lighten-1 text-truncate text-underline"
                            >
                                <span>{{ sellerTitle }}</span>
                            </div>

                            <div class="py-2">
                                <v-row>
                                    <v-col cols="4" class="pr-0">
                                        <v-text-field
                                            v-model="quantity"
                                            :label="$t('quantityCut')"
                                            type="number"
                                            min="1"
                                            color="orange lighten-2"
                                            outlined
                                            dense
                                            hide-details
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="8" class="pt-4"><span>{{ `${$t('total')}: ${orderQuantityValue} ${this.product.pack.unitName}` }}</span></v-col>
                                </v-row>
                            </div>
                            <div class="pa-0 caption">
                                <div class="font-weight-light font-italic pr-3">
                                    {{ $t('packing') }}: <span class="grey--text">{{ realizationForm }}</span>.
                                </div>
                                <div class="font-weight-light font-italic pr-3" v-if="minOrder">
                                    {{ $t('minOrder') }}: <span class="grey--text">{{ minOrder }}</span>.
                                </div>
                            </div>
                            <div>
                                <span class="font-weight-bold">{{ $t('price') }}&colon;&nbsp;</span>
                                <div :class="discount > 0 ? 'red--text text-h6 font-weight-medium' : 'black--text'" class="d-inline px-1">
                                    <span >{{ priceCurrencySymbol }}&nbsp;</span>
                                    <span >{{ productPriceWithDiscount.toLocaleString() }}</span>
                                </div>
                                <div v-if="discount > 0" class="d-inline text-decoration-line-through grey--text">
                                    <small>{{ priceCurrencySymbol }}&nbsp;</small>
                                    <small>{{ productPrice.toLocaleString() }}</small>
                                </div>
                                <span class="caption font-weight-black grey--text px-1">
                                    &lpar;{{ product.price && product.price.withNds ? $t('withNds') : $t('withoutNds') }}&rpar;
                                </span>
                            </div>
                            <div>
                                <policies-dialog
                                    :seller-id="product.seller.id"
                                    :seller-name="sellerTitle"
                                    :product-policies="product.policies"
                                    :price="parseFloat(productPrice)"
                                ></policies-dialog>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card-actions class="pb-4">
                    <v-spacer></v-spacer>
                    <div class="text-center">
                        <v-btn
                            class="mb-2"
                            color="main--yellow white--text"
                            dark
                            depressed
                            @click="pushAddNCloseDialog"
                        >
                            {{ `${$t('add')} ${$t('toBasket')}` }}
                        </v-btn>
                    </div>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="snackOpen"
            top
            :timeout="5000"
        >
            <div class="d-flex flex-column">{{ snackText }}</div>
            <div class="d-flex justify-center">
                <v-btn
                    text
                    link
                    class="text--main-yellow"
                    :to="{name: 'Basket'}"
                >
                    {{ `${$t('goTo')} ${$t('toBasket')}`}}
                </v-btn>
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    icon
                    v-bind="attrs"
                    @click="closeSnack"
                >
                    <v-icon color="grey">close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import PoliciesDialog from "../../../views/front/basket/v-2/PoliciesDialog";
import ActionAccess from "../../mixins/ActionAccess";
import AppSnack from "../../../general/mixins/AppSnack";
import Auth from "../../mixins/Auth";
import DialogRemote from "../../mixins/DialogRemote";
import NoLoginBasket from "../../mixins/NoLoginBasket";
import ThumbsProducts from '../../mixins/ThumbsProducts'
import BasketService from "../../services/api/BasketService";
import { getPercentNumber } from '../../../utils';

export default {
    name: "AddToBasketDialog",
    components: {
        PoliciesDialog
    },
    mixins: [
        ActionAccess,
        Auth,
        DialogRemote,
        NoLoginBasket,
        ThumbsProducts,
        AppSnack
    ],
    props: {
        product: {
            type: Object,
            default: () => {
                return {};
            }
        },
    },
    data () {
        return {
            calculatedPrice: null,
            quantity: this.getQuantityVal(),
        }
    },
    computed: {
        formattedProduct() {
            return {
                id: this.product.id,
                quantity: this.quantity
            }
        },
        orderQuantityValue() {
            let quantity = this.quantity ? parseFloat(this.quantity) : null;

            if (!quantity) return null;
            let value = this.product.pack.value * quantity;
            if(this.packData.minValue > 1){
                value *= this.packData.minValue;
            }
            return value ? value.toFixed(2) : '';
        },
        packData() {
            return this.product.pack || {};
        },
        minOrder() {
            if (!this.packData.minValue) return '';
            if(this.packData.minPackName) {
                return `${this.packData.minPackName.toLowerCase()} (${this.packData.minValue} ${this.packData.packName.toLowerCase()})`;
            } else {
                return `${this.packData.minValue} ${this.packData.packName.toLowerCase()}`;
            }
        },
        realizationForm() {
            if (!this.packData) return '';
            return `${this.packData.packName.toLowerCase()} ${this.packData.value} ${this.packData.unitName}`;
        },
        discount() {
            let discount = this.product.price.discountPercent > 0 ? this.product.price.discountPercent  : 0;
            discount += this.policiesDiscount;
            return discount;
        },
        policiesDiscount() {
            let percent = 0
            this.product.policies.amountPolicy.forEach(item=>{
                if (this.productPrice >= item.minSum && this.productPrice <= item.maxSum) {
                    percent = item.discount;
                }
            });
            return percent;
        },
        priceCurrencySymbol() {
            let currencySymbol;

            this.$store.getters.currencies.forEach(item=>{
                if(item.name === this.$store.getters.appCurrency) {
                    currencySymbol = item.symbol;
                }
            })
            return currencySymbol;
        },
        productPrice() {
            const currency = this.$store.state.appCurrency;
            let price = this.product.price[currency] ? this.product.price[currency] : this.product.price.value;
            price *= this.orderQuantityValue;

            return price || this.$t('contractual');
        },
        productPriceWithDiscount() {
            const price = getPercentNumber(this.productPrice, this.discount);

            return price || this.$t('contractual');
        },
        sellerTitle() {
            if(this.product.seller.name){
                return this.product.seller.name
            }
            return (this.product.seller.companyName) ? this.product.seller.companyName : `${this.product.seller.firstName} ${this.product.seller.lastName}`
        },
    },
    methods: {
        getQuantityVal() {
            const basket = (this.isUserLoggedIn) ? this.$store.getters.basketItems : this.$store.getters.noLoginBasket;

            const value = basket.find(item => item.productId === this.product.id)
            return (value !== undefined) ? value.quantity : 1;
        },
        pushAddNGoToBasket() {
            if (window.fbq) {
                window.fbq('track', 'AddToCart', {
                    content_name: this.product.productName
                } );
            }
            if(this.isUserLoggedIn) {
                BasketService.updateList([this.formattedProduct])
                    .then(res => {
                        this.$store.commit('setBasket', res.data);
                        this.$router.push({ name: 'Basket' })
                    })
                    .catch(err => console.log(err));
            } else {
                this.addToNoLoginBasket([this.formattedProduct]);
                this.$router.push({ name: 'Basket'})
            }
        },
        pushAddNCloseDialog() {
            if (window.fbq) {
                window.fbq('track', 'AddToCart', {
                    content_name: this.product.productName
                });
            }
            this.setSnackText( `${this.orderQuantityValue} ${this.product.pack.unitName} ${this.product.productName}
             ${this.$t('fromCompany')} ${this.sellerTitle} ${this.$t('addToBasketSuccess')}` );

            if(this.isUserLoggedIn) {
                BasketService.updateList([this.formattedProduct])
                    .then(res => {
                        this.$store.commit('setBasket', res.data);
                        this.$emit('close')
                        this.openSnack();
                    })
                    .catch(err => console.log(err));
            } else {
                this.addToNoLoginBasket([this.formattedProduct]);
                this.$emit('close')
                this.openSnack();
            }

        },
    },
}
</script>

<style scoped>
.product-img{
    width: 100%;
}
</style>
